/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, Input } from "@angular/core";
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Observer } from 'rxjs';

import { Rule } from "../../../models/rule";
import { RuleCondition, Operator, OperatorUtil, Type } from "../../../models/rule-condition";
import { EmGroup } from "../../../models/em-group";
import { GroupType } from '../../../models/group-type';
import { Property } from '../../../models/property';
import { PropertyType } from '../../../models/property-type';
import { ObjectInstance } from '../../../models/object-instance';
import { TagInstance } from '../../../models/tag-instance';
import { User } from '../../../models/user';

import { EntityService } from "../../../services/entity.service";
import { GroupService } from "../../../services/group.service";
import { ObjectService } from "../../../services/object.service";
import { TagService } from "../../../services/tag.service";
import { UserService } from "../../../services/user.service";
import { switchMap } from "rxjs/operators";

@Component({
  selector: 'app-entity-filter-editor',
  templateUrl: './entity-filter-editor.component.html'
})
export class EntityFilterEditorComponent implements OnInit {

    @Input()
    rule: Rule;
    
    @Input()
    ruleCondition: RuleCondition;

    operators = Operator;
    
    propertyOperators: Operator[] = [];
    entityOperators: Operator[] = [];
    
    showBody: boolean = false;
    
    allEntities: boolean = false;
    specificEntities: boolean = false;
    
    isExistsOperator: boolean = false;
    isCountOperator: boolean = false;
    isGroupOperator: boolean = false;
    
    groups: EmGroup[] = [];
    properties: Property[] = [];
    
    typeaheadValue: string;
    typeaheadLoading: boolean;
    typeaheadNoResults: boolean;
    entities: Observable<any>;

    constructor(private groupService: GroupService,
        private entityService: EntityService,
        private objectService: ObjectService,
        private tagService: TagService,
        private userService: UserService) {}
    
    ngOnInit(): void {
        this.propertyOperators = [Operator.EQUAL, Operator.NOT_EQUAL, Operator.LESS_THAN, Operator.GREATER_THAN];
//        this.entityOperators = [Operator.EXISTS, Operator.NOT_EXISTS, Operator.COUNT_EQUAL, Operator.COUNT_GREATER_THAN,
//            Operator.COUNT_LESS_THAN, Operator.IN_GROUP, Operator.NOT_IN_GROUP];
        this.entityOperators = OperatorUtil.getOperatorsForPropertyType(this.ruleCondition == null? null : this.ruleCondition.propertyType);
        this.loadProperties();

        this.showBody = this.ruleCondition != null && this.ruleCondition.id != null ? false : true;
        
        //KHT 08/17/2021 - updated to new ngx-bootstrap standard
        this.entities = new Observable((observer: Observer<string>) => {
            observer.next(this.typeaheadValue);
        }).pipe(
            switchMap((query: string) => {
                if(query){
                    return this.search(query);
                }
            })
        )
        
        
        if (this.ruleCondition.sourceType == Type.USER) {
            this.loadReferenceValue();
        }
    }
    
    getEntityLabel(): string {
        if (this.ruleCondition.property == null) {
            return this.ruleCondition.sourceType;
        }
        else {
            return this.ruleCondition.sourceType + " " + this.ruleCondition.property;
        }
    }
    
    getValueLabel(): string {
        if (this.ruleCondition.sourceType == Type.USER) {
            return this.typeaheadValue;
        }
        else if (this.ruleCondition.propertyValue != null) {
            return this.ruleCondition.propertyValue   
        }
        else {
            return this.ruleCondition.value;
        }
    }
    
    getOperatorLabel(operator: Operator): string {
        return OperatorUtil.getOperatorLabel(operator);
    }

    onPropertyChange(property: any): void {
        console.log(property);
        if (property === undefined) {
            this.entityOperators = OperatorUtil.getOperatorsForPropertyType(null);
            this.ruleCondition.property = null;            
        } else if (property != null) {
            this.entityOperators = OperatorUtil.getOperatorsForPropertyType(property.type);
            this.ruleCondition.property = property.property;
        }
        
        this.ruleCondition.operator = this.entityOperators[0];
    }
    
    onOperatorChange(operator: Operator): void {
        this.ruleCondition.operator = operator;
        
        switch (operator) {
            case Operator.EXISTS:
            case Operator.NOT_EXISTS:
                this.isExistsOperator = true;
                this.isGroupOperator = false;
                
                break;
            case Operator.IN_GROUP:
            case Operator.NOT_IN_GROUP:
                this.isExistsOperator = false;
                this.isGroupOperator = true;
                
                break;
            default:
                this.isExistsOperator = false;
                this.isGroupOperator = false;
        }
    }

    onRemove(value): void {
        const index = this.rule.conditions.indexOf(value);
        
        if (index > -1) {
            this.rule.conditions.splice(index, 1);
        }
    }

    async loadGroups(): Promise<void> {
        if (this.groups.length == 0) {
            var groupType: GroupType;
            
            if (this.ruleCondition.sourceType == Type.TAG) {
                groupType = GroupType.TAG;
            }
            else if (this.ruleCondition.sourceType == Type.OBJECT) {
                groupType = GroupType.OBJECT;
            }
            else if (this.ruleCondition.sourceType == Type.USER) {
                groupType = GroupType.USER;
            }
            
            this.groups = await this.groupService.getAllGroupsForGroupType(groupType);
        }
    }

    async loadProperties(): Promise<void> {
        if (this.properties.length == 0) {
            switch (this.ruleCondition.sourceType) {
                case Type.OBJECT:
                    this.properties = [];
                    
                    break;
                case Type.TAG:
                    this.properties = await this.tagService.getAllTagProperties();
                    
                    break;
                case Type.USER:
                    this.properties = [];
                    
                    break;
            }
        }
    }

    async loadReferenceValue(): Promise<void> {
        if (this.ruleCondition.value != null) {
            switch (this.ruleCondition.sourceType) {
                case Type.OBJECT:
                    const object: ObjectInstance = await this.objectService.getObjectInstanceForId(Number(this.ruleCondition.value));
                    if (object != null) {
                        this.typeaheadValue = object.name;
                    }
                    break;
                case Type.TAG:
                    const tag: TagInstance = await this.tagService.getTagInstanceById(this.ruleCondition.propertyValue);
                    if (tag != null) {
                        this.typeaheadValue = tag.name;
                    }
                    break;
                case Type.USER:
                    const user: User = await this.userService.getUserForId(Number(this.ruleCondition.value));
                    if (user != null) {
                        this.typeaheadValue = user.firstName + " " + user.lastName;
                    }
                    break;
            }
        }
    }
    
    changeTypeaheadLoading(e: boolean): void {
        this.typeaheadLoading = e;
    }
    
    typeaheadOnSelect(e: TypeaheadMatch) {
        console.log('Selected value: ', e.item);
        this.ruleCondition.value = e.item.id;
    }
    
    search(token: string) {
        var propertyType: PropertyType;
        
        if (this.ruleCondition.operator != Operator.EQUAL && this.ruleCondition.operator != Operator.NOT_EQUAL) {
            this.ruleCondition.value = token;
        }
        
        if (this.ruleCondition.propertyType == null) {
            if (this.ruleCondition.sourceType == Type.TAG) {
                propertyType = PropertyType.TAG_REFERENCE;
            }
            else if (this.ruleCondition.sourceType == Type.OBJECT) {
                propertyType = PropertyType.OBJECT_REFERENCE;
            }
            else if (this.ruleCondition.sourceType == Type.USER) {
                propertyType = PropertyType.USER_REFERENCE;
            }
        }
        else {
            propertyType = this.ruleCondition.propertyType;
        }
        
        if (propertyType == PropertyType.USER_REFERENCE) {
            return this.entityService.searchUser(token);
        }
        else {
            return this.entityService.search(propertyType, token);
        }
    }
}
